import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { EditDoubleStaffing } from "../ActivityOccurrence/EditDoubleStaffing";
import { EditButton } from "../ActivityOccurrence/EditButton";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { useCurrentlyEditing } from "../ActivityOccurrence/useCurrentlyEditing";
import { EditTitle } from "../ActivityOccurrence/EditTitle";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { EditDescription } from "../ActivityOccurrence/EditDescription";
import { EditMeasurements } from "../ActivityOccurrence/EditMeasurements";
import MeasurementChip from "@/components/Chips/MeasurementChip";
import { EditHidden } from "../ActivityOccurrence/EditHidden";
import { EditRequiredCompetences } from "../ActivityOccurrence/EditRequiredCompetences";
import {
  useActivity,
  useActivityOccurrence,
  type IOneTimeActivitySchedule,
} from "@/api/Activities";
import { ActivitySchedule } from "../ActivityOccurrence/ActivitySchedule";
import {
  getActivityCategoryConfig,
  isEmployeePerformed,
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import styles from "./ActivityInformation.module.scss";
import { EditContainer } from "../ActivityOccurrence/EditContainer";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { ActivityEndDate } from "../ActivityOccurrence/ActivityEndDate";
import { EditDuration } from "../ActivityOccurrence/EditDuration";
import {
  DurationText,
  InvoicingCodes,
} from "../ActivityOccurrence/ActivityOccurrenceInformation";
import { EditTime } from "../ActivityOccurrence/EditTime";
import { dateName } from "@/Utils/DateUtils";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { getActivityOccurrenceIdFromActivitySchedule } from "./getActivityOccurrenceIdFromActivitySchedule";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";

export const ActivityInformation = ({ activityId }: { activityId: string }) => {
  const { t } = useLingui();
  const { currentlyEditing, setCurrentlyEditing } = useCurrentlyEditing();

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const { data: activity, isPending, isError, error } = useActivity(activityId);

  const { organization } = useUserProfile();

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsInvoicingCode: activityAllowsInvoicingCode,
    allowsDuration,
    allowsDoubleStaffing,
  } = getActivityCategoryConfig({
    category: activity.category,
    activityHasPatient: activity.patientId !== undefined,
  });

  const showActivityDuration = allowsDuration && showDuration;
  const activityMayHaveInvoicingCodes =
    activityAllowsInvoicingCode && organizationUsesInvoicingCodes(organization);

  return (
    <div className={styles.activityInfo}>
      <section className={styles.informationSection}>
        <div className={styles.editableSection}>
          <Heading level="h3">
            <Trans>Titel</Trans>
          </Heading>
          <EditButton
            attribute="edit-title"
            isEditing={currentlyEditing === "title"}
            toggleIsEditing={() =>
              setCurrentlyEditing(currentlyEditing === "title" ? null : "title")
            }
          />
        </div>
        {currentlyEditing === "title" ? (
          <EditContainer>
            <EditTitle
              currentTitle={activity.title}
              onSuccess={() => setCurrentlyEditing(null)}
              activityId={activity.id}
            />
          </EditContainer>
        ) : (
          <Text element="p">{activity.title}</Text>
        )}
      </section>
      <section className={styles.informationSection}>
        <div className={styles.editableSection}>
          <Heading level="h3">
            <Trans>Beskrivning</Trans>
          </Heading>
          <EditButton
            attribute="edit-description"
            isEditing={currentlyEditing === "description"}
            toggleIsEditing={() =>
              setCurrentlyEditing(
                currentlyEditing === "description" ? null : "description",
              )
            }
          />
        </div>
        {currentlyEditing === "description" ? (
          <EditContainer>
            <EditDescription
              currentDescription={activity.description}
              onSuccess={() => setCurrentlyEditing(null)}
              activityId={activity.id}
            />
          </EditContainer>
        ) : (
          <TextWithLineBreaks>{activity.description}</TextWithLineBreaks>
        )}
      </section>
      {"measurements" in activity ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Mätvärden</Trans>
            </Heading>
            <EditButton
              attribute="edit-measurements"
              isEditing={currentlyEditing === "measurements"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "measurements" ? null : "measurements",
                )
              }
            />
          </div>
          {currentlyEditing === "measurements" ? (
            <EditContainer>
              <EditMeasurements
                currentMeasurements={activity.measurements}
                onSuccess={() => setCurrentlyEditing(null)}
                activityId={activity.id}
              />
            </EditContainer>
          ) : (
            <ul className={styles.chips}>
              {activity.measurements.map((measurement) => (
                <li key={measurement}>
                  <MeasurementChip measurement={measurement} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {activity.recurring && "times" in activity.schedule ? (
        <>
          <ActivityEndDate
            activityId={activityId}
            status={activity.status}
            schedule={activity.schedule}
          />
        </>
      ) : null}

      {activity.recurring ? (
        <ActivitySchedule
          activityId={activityId}
          schedule={activity.schedule}
        />
      ) : activity.schedule && "time" in activity.schedule ? (
        <OneTimeActivitySchedule
          activityId={activityId}
          schedule={activity.schedule}
        />
      ) : null}

      {showActivityDuration ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Tidsåtgång</Trans>
            </Heading>
            <EditButton
              attribute="edit-duration"
              label={t`Ändra`}
              isEditing={currentlyEditing === "duration"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "duration" ? null : "duration",
                )
              }
            />
          </div>
          {currentlyEditing === "duration" ? (
            <EditContainer>
              <EditDuration
                currentDuration={activity.duration}
                onSuccess={() => {
                  setCurrentlyEditing(null);
                }}
                activityId={activityId}
              />
            </EditContainer>
          ) : (
            <Text element="p">
              <DurationText duration={activity.duration} />
            </Text>
          )}
        </section>
      ) : null}

      {allowsHidden ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Dold för patienten</Trans>
            </Heading>
            <EditButton
              attribute="edit-hidden"
              isEditing={currentlyEditing === "hidden"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "hidden" ? null : "hidden",
                )
              }
            />
          </div>
          {currentlyEditing === "hidden" ? (
            <EditContainer>
              <EditHidden
                currentHidden={activity.hidden}
                onSuccess={() => setCurrentlyEditing(null)}
                activityId={activity.id}
              />
            </EditContainer>
          ) : (
            <Text element="p">{activity.hidden ? t`Ja` : t`Nej`}</Text>
          )}
        </section>
      ) : null}
      {allowsCompetence && isEmployeePerformed(activity) ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Kompetensbehov</Trans>
            </Heading>
            <EditButton
              attribute="edit-requiredCompetences"
              isEditing={currentlyEditing === "requiredCompetences"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "requiredCompetences"
                    ? null
                    : "requiredCompetences",
                )
              }
            />
          </div>
          {currentlyEditing === "requiredCompetences" ? (
            <EditContainer>
              <EditRequiredCompetences
                currentRequiredCompetences={activity.requiredCompetences}
                isRequired={requiresCompetence}
                onSuccess={() => setCurrentlyEditing(null)}
                activityId={activity.id}
              />
            </EditContainer>
          ) : (
            <ul className={styles.chips}>
              {activity.requiredCompetences.map((competence) => (
                <li key={competence}>
                  <CompetenceChip competence={competence} state="neutral" />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      {allowsDoubleStaffing && "doubleStaffing" in activity ? (
        <section className={styles.informationSection}>
          <div className={styles.editableSection}>
            <Heading level="h3">
              <Trans>Dubbelbemanning</Trans>
            </Heading>
            <EditButton
              attribute="edit-doubleStaffing"
              isEditing={currentlyEditing === "doubleStaffing"}
              toggleIsEditing={() =>
                setCurrentlyEditing(
                  currentlyEditing === "doubleStaffing"
                    ? null
                    : "doubleStaffing",
                )
              }
            />
          </div>
          {currentlyEditing === "doubleStaffing" ? (
            <EditContainer>
              <EditDoubleStaffing
                currentDoubleStaffing={activity.doubleStaffing}
                onSuccess={() => setCurrentlyEditing(null)}
                activityId={activity.id}
              />
            </EditContainer>
          ) : (
            <Text element="p">{activity.doubleStaffing ? t`Ja` : t`Nej`}</Text>
          )}
        </section>
      ) : null}

      {activityMayHaveInvoicingCodes ? (
        <InvoicingCodes activity={activity} />
      ) : null}
    </div>
  );
};

const OneTimeActivitySchedule = ({
  activityId,
  schedule,
}: {
  activityId: string;
  schedule: IOneTimeActivitySchedule;
}) => {
  const { t } = useLingui();
  const occurrenceId = getActivityOccurrenceIdFromActivitySchedule(schedule);

  const {
    data: activityOccurrence,
    isPending,
    isError,
  } = useActivityOccurrence(activityId, occurrenceId);
  const { currentlyEditing, setCurrentlyEditing } = useCurrentlyEditing();

  if (isPending || isError) {
    return (
      <section className={styles.informationSection}>
        <div className={styles.editableSection}>
          <Heading level="h3">
            <Trans>Datum och tid</Trans>
          </Heading>
          {isPending ? (
            <Loading message={t`Hämtar information om datum och tid`} />
          ) : (
            <ErrorMessage
              message={t`Kunde inte hämta information om datum och tid. Prova att ladda om sidan. Kontakta support om felet kvarstår.`}
            />
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.informationSection}>
      <div className={styles.editableSection}>
        <Heading level="h3">
          <Trans>Datum och tid</Trans>
        </Heading>
        <EditButton
          attribute="edit-time"
          isEditing={currentlyEditing === "time"}
          toggleIsEditing={() =>
            setCurrentlyEditing(currentlyEditing === "time" ? null : "time")
          }
        />
      </div>
      {currentlyEditing === "time" ? (
        <EditContainer>
          <EditTime
            activityId={activityId}
            occurrenceId={occurrenceId}
            currentDateTime={activityOccurrence.start}
            currentTimeOfDay={activityOccurrence.timeOfDay}
            currentTimespan={activityOccurrence.timespan}
            onSuccess={() => setCurrentlyEditing(null)}
          />
        </EditContainer>
      ) : (
        <div className={styles.multipleLines}>
          <Text element="span">{dateName(activityOccurrence.start)}</Text>
          <Text element="span">
            {activityOccurrence.timeOfDay === timeOfDaySchema.enum.Any
              ? t(timeOfDayDictionary.Any.long)
              : formattedTimeSpan(
                  activityOccurrence.start,
                  activityOccurrence.end,
                )}
          </Text>
        </div>
      )}
    </section>
  );
};
